
import {
  ComponentPublicInstance,
  computed,
  defineComponent,
  onBeforeUpdate,
  onMounted,
  PropType,
  ref,
} from 'vue';
import animateScrollTo from 'animated-scroll-to';
import { useRouter } from 'vue-router';
import { useStore } from '@/store';
import StoryHero from '@/components/StoryHero.vue';
import StoryListingComponent from '@/components/StoryListing.vue';
import { StoryListing } from '@/types';

export default defineComponent({
  components: {
    StoryHero,
    StoryListingComponent,
  },
  props: {
    stories: {
      type: Array as PropType<StoryListing[]>,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();
    const store = useStore();
    const active = ref('');
    const overlay = ref<StoryListing | false>(false);

    const hero = computed(() => props.stories[0]);
    const supporting = computed(() => props.stories.slice(1));

    const heroRef = ref<ComponentPublicInstance | null>(null);
    const listRef = ref<HTMLElement | null>(null);

    store.commit.setNavLight(props.stories[0]?.hero.lightText);

    const onActivateHero = (el: HTMLElement, key: string) => {
      animateScrollTo(el.offsetTop, {
        cancelOnUserAction: false,
        maxDuration: 1000,
      }).then((hasScrolledToPosition) => {
        if (hasScrolledToPosition) {
          document.body.classList.add('overflow-hidden');
          store.commit.setNavLight(false);
          active.value = key;
        }
      });
    };

    const onActivateListing = (story: StoryListing) => {
      document.body.classList.add('overflow-hidden');
      store.commit.setNavLight(false);
      active.value = story._id;
      overlay.value = story;
    };

    const onAnimated = (story: StoryListing) => {
      document.body.classList.remove('overflow-hidden');
      router.push(story.link);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.some((entry) => {
          if (entry.isIntersecting) {
            const isHero = entry.target === heroRef.value?.$el;
            if (entry.intersectionRatio > 0.5 && !isHero) {
              store.commit.setNavLight(false);
              return true;
            } else if (entry.intersectionRatio < 0.5 && isHero) {
              store.commit.setNavLight(hero.value.hero.lightText);
              return true;
            }
          }
        });
      },
      { threshold: [0.05, 0.95] }
    );

    onMounted(() => {
      if (heroRef.value && listRef.value) {
        observer.observe(heroRef.value.$el);
        observer.observe(listRef.value);
      }
    });
    onBeforeUpdate(() => {
      heroRef.value = null;
      listRef.value = null;
    });
    return {
      active,
      overlay,
      hero,
      heroRef,
      onAnimated,
      listRef,
      onActivateHero,
      onActivateListing,
      supporting,
    };
  },
});
